import { BACKEND_DATETIME_FORMAT, BACKEND_DATE_FORMAT } from 'config/Vars'
import moment from 'moment'
import momentTimezone from 'moment-timezone'
import type { $Keys } from 'utility-types'

import i18n from 'modules/core/lib/i18n'

export type DayOfWeekIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6

export const FORMAT = {
  backend: BACKEND_DATE_FORMAT,
  backend_datetime: BACKEND_DATETIME_FORMAT,
  backend_datetime_without_timezone: 'YYYY-MM-DDTHH:mm:ss',
  ui_date_with_time: 'L HH:mm',
  ui: 'L',
  ui_abbreviated_month_year: 'D MMM YYYY',
  ui_full_month_year: 'D MMMM YYYY',
  ui_short: 'ddd D',
  ui_time: 'HH:mm',
  ui_long: 'dddd, MMMM D',
  ui_dmy_long: 'Do MMM YYYY',
  ui_dm_long: 'Do MMM',
  ui_month_year: 'MMM YYYY',
  ui_long_with_year: 'ddd Do, MMMM YYYY',
  ui_short_with_time: 'DD/MM/YYYY HH:mm',
  ui_day_initials_format: 'D',
  ui_time_date_dm: 'Do MMM HH:mm',
  ui_date_day_month_year: 'DD/MM/YY',
  ui_full_weekday_long_date_with_year: 'dddd Do, MMMM YYYY',
  ui_month_day_time: 'MMM Do, h:mma',
}
export type FormatTypes = $Keys<typeof FORMAT>

const Months = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
]

export function nowBackendDateTime() {
  return dateToString(moment(), 'backend_datetime')
}

export function stringToDate(date: string, format: FormatTypes): moment.Moment {
  return moment(date, FORMAT[format], format !== 'backend_datetime_without_timezone')
}

export function getNormalizedToday(format: FormatTypes = 'backend'): Date {
  const todayString = i18nDateToString(new Date(), format)
  return stringToDate(todayString, format).toDate()
}

type GetUIDurationParams = {
  date: moment.Moment
  fromDate?: moment.Moment
  withPrefixOrSuffix?: boolean
}

export function getUIDuration({ date, fromDate, withPrefixOrSuffix }: GetUIDurationParams): string {
  const startDate = fromDate ?? moment()

  // this is to show "1 day" instead "a day", or "1 month" instead of "a month"
  const formatUnit = (value: number, unit: Intl.RelativeTimeFormatUnit) => {
    const formatter = new Intl.RelativeTimeFormat(i18n.locale, {
      numeric: 'always',
    })

    return formatter
      .formatToParts(value, unit)
      .slice(-2)
      .map((p) => p.value)
      .join('')
  }

  const isFuture = date.isAfter(startDate)

  // Determine the earlier and later dates for accurate calculation
  const earlierDate = isFuture ? startDate.clone() : date.clone()
  const laterDate = isFuture ? date.clone() : startDate.clone()

  // Calculate years, then add that time to the earlier date
  const years = laterDate.diff(earlierDate, 'years')
  earlierDate.add(years, 'years')

  // Calculate months, then add that time to the earlier date
  const months = laterDate.diff(earlierDate, 'months')
  earlierDate.add(months, 'months')

  // Calculate remaining days
  const days = laterDate.diff(earlierDate, 'days')

  const localeData = moment.localeData()

  const result = []
  if (years > 0) {
    const yearsLabel =
      years === 1 ? formatUnit(1, 'year') : localeData.relativeTime(years, true, 'yy', false)
    result.push(yearsLabel)
  }
  if (months > 0) {
    const monthsLabel =
      months === 1 ? formatUnit(1, 'month') : localeData.relativeTime(months, true, 'MM', false)
    result.push(monthsLabel)
  }
  if (days > 0) {
    const daysLabel =
      days === 1 ? formatUnit(1, 'day') : localeData.relativeTime(days, true, 'dd', false)
    result.push(daysLabel)
  }

  let output = result.join(', ')

  if (result.length === 0) {
    return i18n.t('common.less_than_a_day')
  } else if (result.length > 1) {
    output = i18n.t('common.and', {
      initial: result.slice(0, result.length - 1).join(', '),
      last: result[result.length - 1],
    })
  }

  if (withPrefixOrSuffix) {
    output = `${localeData.pastFuture(isFuture ? 1 : -1, output)}`
  }

  return output
}

/**
 * DO NOT USE. It will be removed.
 *
 * @deprecated Use `i18nDateToString` instead, which uses translateable formats.
 */
export function dateToString(date: moment.Moment | Date, format: FormatTypes): string {
  const momentDate = date instanceof Date ? moment(date) : date
  // We have declared BACKEND_DATETIME_FORMAT on config/Vars as
  // moment.ISO_8601. Unfortunately moment only knows how to use that in order
  // to parse dates, but not to format them. The moment's documentation
  // explicits that in order to format the in the format that we want, this has
  // to be used.
  //
  // https://momentjs.com/docs/#/displaying/as-iso-string/
  if (format === 'backend_datetime') {
    return momentDate.toISOString()
  }

  const formatString = FORMAT[format]
  return momentDate.format(formatString)
}

export function i18nDateToString(date: moment.Moment | Date, format: FormatTypes): string {
  const momentDate = date instanceof Date ? moment(date) : date
  // We have declared BACKEND_DATETIME_FORMAT on config/Vars as
  // moment.ISO_8601. Unfortunately moment only knows how to use that in order
  // to parse dates, but not to format them. The moment's documentation
  // explicits that in order to format the in the format that we want, this has
  // to be used.
  //
  // https://momentjs.com/docs/#/displaying/as-iso-string/
  if (format === 'backend') {
    return dateToString(momentDate, format)
  }
  if (format === 'backend_datetime') {
    return momentDate.toISOString()
  }

  const formatString = getDateFormat(format)

  return momentDate.format(formatString)
}

const getDateFormat = (format: FormatTypes) => {
  switch (format) {
    case 'backend_datetime_without_timezone':
      // This one shouldn't be translated
      return FORMAT.backend_datetime_without_timezone
    case 'ui':
      return i18n.t('date_formats.ui')
    case 'ui_abbreviated_month_year':
      return i18n.t('date_formats.ui_abbreviated_month_year')
    case 'ui_full_month_year':
      return i18n.t('date_formats.ui_full_month_year')
    case 'ui_short':
      return i18n.t('date_formats.ui_short')
    case 'ui_time':
      return i18n.t('date_formats.ui_time')
    case 'ui_long':
      return i18n.t('date_formats.ui_long')
    case 'ui_dmy_long':
      return i18n.t('date_formats.ui_dmy_long')
    case 'ui_dm_long':
      return i18n.t('date_formats.ui_dm_long')
    case 'ui_month_year':
      return i18n.t('date_formats.ui_month_year')
    case 'ui_long_with_year':
      return i18n.t('date_formats.ui_long_with_year')
    case 'ui_short_with_time':
      return i18n.t('date_formats.ui_short_with_time')
    case 'ui_day_initials_format':
      return i18n.t('date_formats.ui_day_initials_format')
    case 'ui_time_date_dm':
      return i18n.t('date_formats.ui_time_date_dm')
    case 'ui_date_day_month_year':
      return i18n.t('date_formats.ui_date_day_month_year')
    case 'ui_full_weekday_long_date_with_year':
      return i18n.t('date_formats.ui_full_weekday_long_date_with_year')
    case 'ui_month_day_time':
      return i18n.t('date_formats.ui_month_day_time')
    default:
      throw new Error('Invalid format')
  }
}

export function convertDate(date: string, from: FormatTypes, to: FormatTypes): string {
  return dateToString(stringToDate(date, from), to)
}

export function dateBackendDateTimeToRelativeTime(date: string): string {
  return stringToDate(date, 'backend_datetime').fromNow()
}

export function dateBackendToUI(date: string): string {
  return convertDate(date, 'backend', 'ui')
}

// This outputs a date in the format "D/M", but localized
// Copied from: https://stackoverflow.com/questions/44307140/localizing-day-and-month-in-moment-js
export function dateBackendToShortDMUI(date: string): string {
  const today = moment(date).format('L')
  return today
    .replace(moment(date).format('YYYY'), '') // remove year
    .replace(/[рг]\./, '') // remove year letter from RU/UK locales
    .replace(/de$/, '') // remove year prefix from PT
    .replace(/b\.$/, '') // remove year prefix from SE
    .trim() // remove spaces from the start and the end
    .replace(/\/$/g, '') // remove / from the end
    .replace(/-$/g, '') // remove - from the end
}

export function dateBackendDateTimeToUI(date: string): string {
  return convertDate(date, 'backend_datetime', 'ui')
}

export function dateBackendDateTimeToBackend(date: string): string {
  return convertDate(date, 'backend_datetime', 'backend')
}

export function dateUIToBackend(date: string): string {
  return convertDate(date, 'ui', 'backend')
}

export function diffBetweenDates(to: string, from: string): number {
  return stringToDate(to, 'backend').diff(stringToDate(from, 'backend'), 'days')
}

export function diffBetweenDateTimes(to: string, from: string): number {
  return stringToDate(to, 'backend_datetime').diff(
    stringToDate(from, 'backend_datetime'),
    'seconds'
  )
}

export function timeToString(time: moment.Moment): string {
  return time.format(FORMAT.ui_time)
}

export function monthToNumber(month: string): number {
  if (!Months.includes(month.toLowerCase())) {
    throw new Error('Invalid month')
  }

  // the locale is to force moment to use english here
  return moment().locale('en').month(month).get('month')
}

export function monthPeriodToMonthYear(month: number, year: number): string {
  return moment()
    .month(month - 1)
    .year(year)
    .format('MMM YYYY')
}

export function quarterPeriodToMonthYear(quarter: number, year: number): string {
  const monthStart = (quarter - 1) * 3
  const monthEnd = monthStart + 2

  return `${moment().month(monthStart).format('MMM')} - ${moment()
    .month(monthEnd)
    .year(year)
    .format('MMM YYYY')}`
}

export function getMonthsOfYear(): string[] {
  return moment.months()
}

export function formatBackendDateToMonthYear(date: moment.Moment): string {
  return date.format('MMM YYYY')
}

export function formatBackendDateToQuarterMonthYear(date: moment.Moment): string {
  return quarterPeriodToMonthYear(date.quarter(), date.year())
}

export function formatBackendDateToMonthDay(date: string): string {
  return stringToDate(date, 'backend').format('MMM D')
}

export function formatBackendDateToMonthDayLong(date: string): string {
  return stringToDate(date, 'backend').format('MMMM D')
}

export function formatBackendDateToMonthDayYearLong(date: string): string {
  return stringToDate(date, 'backend').format('MMMM D, YYYY')
}

export function formatBackendDateToDayMonthLong(date: string): string {
  return stringToDate(date, 'backend').format('D MMMM')
}

function formatBackendDateToMonth(date: string): string {
  return stringToDate(date, 'backend').format('MMMM')
}

export function formatBackendDateToYear(date: string): string {
  return stringToDate(date, 'backend').format('YYYY')
}

export function dateRange(startDate: string, endDate: string): string {
  return `${formatBackendDateToMonthDay(startDate)} - ${formatBackendDateToMonthDay(endDate)}`
}

export function localTimezone(): string {
  return momentTimezone.tz.guess(true)
}

export function timeToStringWithTimezone(time: moment.Moment, timezone?: string): string {
  if (!timezone) return timeToString(time)

  const employeeLocationTZ = momentTimezone.tz(time, timezone)

  return timeToString(employeeLocationTZ)
}

export function stringToDateWithTimezone(
  date: string,
  format: FormatTypes,
  timezone?: string | null
): moment.Moment {
  if (!timezone) return stringToDate(date, format)

  // change default moment timezone before formatting a string DATE into a moment object
  momentTimezone.tz.setDefault(timezone)

  // Parse a given formatted date to an specific timezone
  const locationTimeZone = momentTimezone.tz(stringToDate(date, format), timezone)
  // Reset moment timezone to browser's local timezone
  momentTimezone.tz.setDefault()

  return locationTimeZone
}

export function dateBackendDateTimeToUITime(date: string): string {
  return convertDate(date, 'backend_datetime', 'ui_time')
}

export const dateBackendDateTimeToUITimeRange = (startTime: string, endTime: string): string => {
  return `${dateBackendDateTimeToUIUtcTime(startTime)} - ${dateBackendDateTimeToUIUtcTime(endTime)}`
}

// ['2024-02-01', '2024-02-01'] => '1 Feb, 2024'
// ['2024-02-01', '2024-02-29'] => '1—29 Feb, 2024'
// ['2024-02-01', '2024-03-01'] => '1 Feb — 1 Mar, 2024'
// ['2024-02-01', '2025-02-01'] => '1 Feb, 2024 — 1 Feb, 2025'
export function formatBackendDateRange(startsAt: string, endsAt: string): string {
  const startDate = stringToDate(startsAt, 'backend')
  const endDate = stringToDate(endsAt, 'backend')

  if (startDate.isSame(endDate)) {
    return startDate.format('D MMM, YYYY')
  } else if (startDate.format('YYYY') !== endDate.format('YYYY')) {
    // Different years
    return `${startDate.format('D MMM, YYYY')} — ${endDate.format('D MMM, YYYY')}`
  } else if (startDate.format('MM') !== endDate.format('MM')) {
    // Same year, different months
    return `${startDate.format('D MMM')} — ${endDate.format('D MMM, YYYY')}`
  } else {
    // Same month and year
    return `${startDate.format('D')}—${endDate.format('D MMM, YYYY')}`
  }
}

export const dateBackendDateTimeToUIUtcTime = (time: string, format = FORMAT.ui_time) =>
  `${moment.utc(time).format(format)}`

export function formatBackendDateToDayMonthYear(date: string, fullMonthName = false): string {
  return fullMonthName
    ? stringToDate(date, 'backend').format(FORMAT.ui_full_month_year)
    : stringToDate(date, 'backend').format(FORMAT.ui_abbreviated_month_year)
}

export const dateRangeWithoutDay = (startDate: moment.Moment, endDate: moment.Moment): string => {
  if (formatBackendDateToMonthYear(startDate) === formatBackendDateToMonthYear(endDate)) {
    return startDate.format('MMMM YYYY')
  }
  if (startDate.year() === endDate.year()) {
    return startDate.format('MMM').concat(' - ', formatBackendDateToMonthYear(endDate))
  }
  return formatBackendDateToMonthYear(startDate).concat(
    ' - ',
    formatBackendDateToMonthYear(endDate)
  )
}

export function dateRangeWithYear(startDate: string, endDate: string): string {
  return `${formatBackendDateToDayMonthYear(startDate)} - ${formatBackendDateToDayMonthYear(
    endDate
  )}`
}

export function isCurrentYear(date: moment.Moment) {
  return moment().year() === moment(date).year()
}

export function isBackendDateTimeInFuture(date: string) {
  return moment().isBefore(stringToDate(date, 'backend_datetime'))
}

export function isBackendDateOverdue(date: string) {
  return stringToDate(date, 'backend_datetime').isBefore(moment(), 'day')
}

export function isBackendDateToday(date: string) {
  return stringToDate(date, 'backend_datetime').isSame(moment(), 'day')
}

export interface CalendarInfo {
  calendar: string
  calendarFormat: string
}

export function formatBackendDateTimeToCalendar(date: string): CalendarInfo {
  const dateMoment = stringToDate(date, 'backend_datetime')
  const calendar = dateMoment.calendar()
  const calendarFormat = moment.calendarFormat(dateMoment, moment().startOf('day'))

  return { calendar, calendarFormat }
}

export function formatSecondsToHoursAndMinutes(seconds: number): string {
  const duration = moment.duration(seconds, 'seconds')
  const hours = Math.floor(duration.asHours())
  const minutes = Math.floor(duration.minutes())
  const localeData = moment.localeData()

  if (hours === 0) {
    return localeData.relativeTime(minutes, true, 'mm', false)
  }

  if (minutes === 0) {
    return localeData.relativeTime(hours, true, 'hh', false)
  }

  return `${localeData.relativeTime(hours, true, 'hh', false)} ${localeData.relativeTime(
    minutes,
    true,
    'mm',
    false
  )}`
}
